<template>
  <div class="py-small">
    <div v-if="!loading && dataList.length > 0">
      <ak-cards gutter="small" class="mr-small ml-tiny">
        <ak-cards-item
          v-for="automation in dataList"
          :key="automation._id"
          class="one-whole desk-xl--one-third desk--one-half"
        >
          <ak-card class="h-full">
            <ak-card-body>
              <ak-pack alignment="top" class="one-whole m-none">
                <ak-pack-item class="p-none">
                  <ak-checkbox
                    :model-value="isAutomationChecked(automation._id)"
                    @change="checkAutomation(automation)"
                  />
                  <ak-icon
                    type="form"
                    size="large"
                    color="primary"
                    class="ml-tiny"
                  />
                </ak-pack-item>
                <ak-pack-item grow class="flex flex--wrap p-none pl-tiny">
                  <div class="one-whole flex">
                    <h3
                      class="mb-tiny color-grey color-link-hover text-breakout"
                    >
                      <router-link
                        :to="{
                          name: 'automationEdit',
                          params: { id: automation._id },
                        }"
                      >
                        {{ automation.name }}
                      </router-link>
                    </h3>
                    <div class="min-width-125 ml-auto text-right">
                      <ak-tag
                        v-if="automation.hidden"
                        color="warning"
                        class="mr"
                      >
                        Hidden
                      </ak-tag>
                      <small class="mr-tiny">
                        <strong>{{ $t('Enabled') }}</strong>
                      </small>
                      <ak-switch
                        v-model="automation.status"
                        true-value="enabled"
                        false-value="disabled"
                        @click.stop="toggle(automation)"
                      />
                    </div>
                  </div>
                  <p class="one-whole">
                    <ak-icon type="folder" />
                    {{ automation.folder || '-' }}
                  </p>
                  <p class="one-whole mb-none">
                    {{ automation.description }}
                  </p>
                </ak-pack-item>
              </ak-pack>
            </ak-card-body>
            <ak-card-footer class="flex py-none px-small flex--center">
              <div style="flex-grow: 1">
                {{ $t('Triggered') }}
                <strong>
                  {{
                    automation.triggered
                      ? formatNumber(automation.triggered)
                      : 0
                  }}
                </strong>
                {{ $t('times') }}
              </div>
              <div class="h-full br" />
              <router-link
                class="color-primary p-small"
                :to="{
                  name: 'automationEdit',
                  params: { id: automation._id },
                }"
              >
                <strong>{{ $t('Edit') }}</strong>
              </router-link>
              <div class="h-full br mr-small" />
              <automation-actions :automation="automation" :button="false" />
            </ak-card-footer>
          </ak-card>
        </ak-cards-item>
      </ak-cards>
      <ak-pagination
        v-if="!loading"
        v-model:current="currentPage"
        :total="total"
        :per-page="20"
        class="m-small"
        @change="onPageChange"
      />
    </div>
    <div
      v-if="loading"
      class="one flex flex--center flex--justify-center min-h-600"
    >
      <ak-loading-spinner />
    </div>
    <div v-if="!loading && dataList.length === 0" class="p-huge text-center">
      <ak-icon type="gears" size="huge" class="mt-small" />
      <h3 class="color-grey">
        {{ $t("There aren't any automations here yet") }}
      </h3>
      <ak-button tag="router-link" :to="{ name: 'automationNew' }">
        {{ $t('Create Automation') }}
      </ak-button>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, watch } from 'vue'
import Cards from '@akerolabs/akero-vue/src/components/cards/Cards.vue'
import Card from '@akerolabs/akero-vue/src/components/cards/Card.vue'
import CardsItem from '@akerolabs/akero-vue/src/components/cards/CardsItem.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import CardFooter from '@akerolabs/akero-vue/src/components/cards/CardFooter.vue'
import Checkbox from '@akerolabs/akero-vue/src/components/checkbox/Checkbox.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Switch from '@akerolabs/akero-vue/src/components/switch/Switch.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Pagination from '@akerolabs/akero-vue/src/components/pagination/Pagination.vue'
import LoadingSpinner from '@akerolabs/akero-vue/src/components/loading/LoadingSpinner.vue'
import AutomationActions from './AutomationActions.vue'
import RouterLink from '../../../legacy/router/RouterLink.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Tag from '@akerolabs/akero-vue/src/components/tag/Tag.vue'
import { formatNumber } from '../../../utils/helpers'

export default defineComponent({
  name: 'GridView',
  components: {
    [Cards.name]: Cards,
    [Card.name]: Card,
    [CardsItem.name]: CardsItem,
    [CardBody.name]: CardBody,
    [CardFooter.name]: CardFooter,
    [Checkbox.name]: Checkbox,
    [Icon.name]: Icon,
    [Switch.name]: Switch,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [AutomationActions.name]: AutomationActions,
    [Pagination.name]: Pagination,
    [LoadingSpinner.name]: LoadingSpinner,
    [RouterLink.name]: RouterLink,
    [Button.name]: Button,
    [Tag.name]: Tag,
  },
  props: {
    /**
     * @type {Array}
     * @description The automation data to display
     */
    data: {
      type: Array,
      default: () => [],
    },
    /**
     * @type {Number}
     * @description The total number of automations
     */
    total: {
      type: Number,
      default: 0,
    },
    /**
     * @type {Array}
     * @description The currently selected automations to be used in bulk actions
     */
    selected: {
      type: Array,
      default: () => [],
    },
    /**
     * @type {Boolean}
     * @description Is loading
     */
    loading: {
      type: Boolean,
      default: true,
    },
    /**
     * @type {Number}
     * @description The current page in the pagination
     */
    activePage: {
      type: Number,
      default: 1,
    },
  },
  emits: ['update:data', 'update:selected', 'page', 'toggle'],
  setup(props, { emit }) {
    const dataList = ref(props.data)
    const currentPage = ref(props.activePage)
    const checked = ref([])
    // Watch props to update internals
    watch(
      () => props.data,
      (value) => {
        dataList.value = value
      },
      { deep: true },
    )
    watch(
      () => props.activePage,
      (value) => {
        currentPage.value = value
      },
    )
    watch(
      () => props.selected,
      (value) => {
        checked.value = value
      },
      { deep: true },
    )

    // Watch internals to update props
    watch(
      dataList,
      (value) => {
        emit('update:data', value)
      },
      { deep: true },
    )

    /**
     * @param {String} id The automation id
     * @description Is the current automation selected for bulk actions
     */
    const isAutomationChecked = (id) => {
      const found = checked.value.find((i) => {
        return i._id === id
      })
      if (found) {
        return true
      }
      return false
    }

    /**
     * @param {Object} automation
     * @description Add or remove the given automation from the bulk actions list
     */
    const checkAutomation = (automation) => {
      const temp = [...checked.value]
      if (isAutomationChecked(automation._id)) {
        checked.value = temp.filter((i) => {
          return i._id !== automation._id
        })
      } else {
        temp.push(automation)
        checked.value = temp
      }
      emit('update:selected', checked.value)
    }

    /**
     * @description when the pagination page change
     * emit the event so a fetch can be run
     * @param {Number} p
     */
    const onPageChange = (p) => {
      emit('page', p)
    }

    /**
     * @description when the enable switch is toggled
     * emit the event so the api request can be made
     * @param {Object} row
     */
    const toggle = (row) => {
      emit('toggle', row)
    }

    return {
      dataList,
      formatNumber,
      currentPage,
      onPageChange,
      toggle,
      isAutomationChecked,
      checkAutomation,
    }
  },
})
</script>

<i18n>
{
  "en": {
    "Enabled": "Enabled",
    "Triggered": "Triggered",
    "times": "times",
    "Edit": "Edit",
    "There aren't any automations here yet": "There aren't any automations here yet",
    "Create Automation": "Create Automation"
  },
  "en-US": {
    "Enabled": "Enabled",
    "Triggered": "Triggered",
    "times": "times",
    "Edit": "Edit",
    "There aren't any automations here yet": "There aren't any automations here yet",
    "Create Automation": "Create Automation"
  }
}
</i18n>
